import { useEffect, useMemo, useRef, useState } from "react";
import { AREA_R, layoutMap, mapOf, unit, type MapPlace, type Pt } from "../shared/map";
import type { StoryView } from "../shared/story";
import { skinNow, threadColors } from "./cloth";
import { storyCode } from "./endings";
import { saveMap } from "./mapsave";
import { api } from "./api";

/**
 * The story map, drawn (docs/ideas/loom-reviews-2026-10-18/map.md, step 2: the plain version, before any painted
 * ground). Everything on it is what the story recorded (shared/map.ts): the places the player stood in, along the path
 * they took (a /elsewhere jump dotted, with the time they gave), each place's parts clustered inside it, "you're here",
 * places only heard of as smudges at the edge (never named, never a way there), loose ends as short tufts where they
 * began (or with the player, for what they carry), and a knot where something sudden struck. Memory fades the marks of
 * places not visited in a while, never their names. Drawn at its real size, so labels stay 13px; a label that would
 * collide is hidden and shown on tap. Tapping a place or a tuft selects it; the bar beneath says what you can do.
 */

const FONT_PX = 13;
const PAD = 34;
/** A tuft's dash, its second cue besides colour (Phosphor's ambers are close). */
const DASHES = ["none", "5 3", "1.5 3", "8 3 2 3"];

export const withArticle = (name: string) => (/^(the|a|an)\s/i.test(name) || /^[A-Z]/.test(name) ? name : `the ${name}`);
/** A slightly bowed line between two points, as if inked by hand; the bow is fixed by the two places. */
const bow = (a: Pt, b: Pt, k: number) => {
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, dx = b.x - a.x, dy = b.y - a.y;
  return `M${a.x.toFixed(1)},${a.y.toFixed(1)} Q${(mx - dy * k).toFixed(1)},${(my + dx * k).toFixed(1)} ${b.x.toFixed(1)},${b.y.toFixed(1)}`;
};
interface Box { x: number; y: number; w: number; h: number }
const hit = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

type Sel = { kind: "place"; id: string } | { kind: "thread"; id: string } | null;

/**
 * The painted ground, as the map draws it: the dream's image itself, or for Phosphor the same image re-inked in its
 * two ambers with an ordered (Bayer) dither at low resolution, like an old screen (owner's choice, 2026-10-18).
 */
function useGround(url: string | null, amber: boolean, N = 200): string | null {
  const [src, setSrc] = useState<string | null>(amber ? null : url);
  useEffect(() => {
    if (!url || !amber) { setSrc(url); return; }
    let live = true;
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas"); c.width = N; c.height = N;
      const g = c.getContext("2d")!; g.drawImage(img, 0, 0, N, N);
      const d = g.getImageData(0, 0, N, N), px = d.data;
      let lo = 255, hi = 0;
      for (let k = 0; k < px.length; k += 4) { const v = px[k]!; if (v < lo) lo = v; if (v > hi) hi = v; }
      const B = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        const k = (y * N + x) * 4, v = ((px[k]! - lo) / Math.max(1, hi - lo)) * 0.78, t = (B[(y % 4) * 4 + (x % 4)]! + 0.5) / 16;
        const [r, gg, b] = v > t + 0.32 ? [240, 168, 58] : v > t * 0.9 ? [110, 75, 24] : [12, 9, 4];
        px[k] = r!; px[k + 1] = gg!; px[k + 2] = b!; px[k + 3] = 255;
      }
      g.putImageData(d, 0, 0);
      if (live) setSrc(c.toDataURL("image/png"));
    };
    img.src = url;
    return () => { live = false; };
  }, [url, amber, N]);
  return src;
}

/** A plate pinned on the map (dithered to amber in Phosphor, like the ground). */
function PinImage({ src, x, y, size, amber }: { src: string; x: number; y: number; size: number; amber: boolean }) {
  const shown = useGround(src, amber, 72);
  return shown ? <image href={shown} x={x} y={y} width={size} height={size} preserveAspectRatio="xMidYMid slice" style={amber ? { imageRendering: "pixelated" } : undefined} /> : null;
}

export function NoirMap({ view, onLook, onFill, onPull, onClose, compact = false, onOpen, size, fullscreen = false }: {
  view: StoryView;
  onLook?(name: string): void;
  onFill?(text: string): void;
  onPull?(t: StoryView["threads"][number]): void;
  onClose(): void;
  /** A small map beside the story (a theme's margin): no bar or key; a tap opens the full map in the journal. */
  compact?: boolean;
  onOpen?(): void;
  /** Full screen (BigMap): drawn at this size (the screen times the zoom), with room to spread out; no save or key. */
  size?: { w: number; h: number };
  fullscreen?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [measured, setW] = useState(0);
  const w = size?.w ?? measured;
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.round(el.clientWidth)));
    ro.observe(el);
    setW(Math.round(el.clientWidth));
    return () => ro.disconnect();
  }, []);
  const h = size?.h ?? (compact ? Math.round(Math.min(Math.max(w * 0.6, 150), 180)) : Math.round(Math.min(Math.max(w * 0.78, 220), 360)));
  const data = useMemo(() => mapOf(view), [view]);
  const layout = useMemo(() => layoutMap(data, view.id), [data, view.id]);
  const skin = skinNow();
  const colors = useMemo(() => threadColors(view, skin), [view, skin]);
  const ground = useGround(view.ground ? `/api/stories/${view.id}/ground?v=${view.ground.drawnAt}` : null, skin === "phosphor");
  // A new ground develops like a print, once (remembered on this device).
  const [develop] = useState(() => {
    if (!view.ground) return false;
    try { const k = `loom:ground:${view.id}`; if (Number(localStorage.getItem(k) ?? 0) < view.ground.drawnAt) { localStorage.setItem(k, String(view.ground.drawnAt)); return true; } } catch { /* no memory: no ceremony */ }
    return false;
  });
  const svgRef = useRef<SVGSVGElement>(null);
  const [names, setNames] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sel, setSelState] = useState<Sel>(null);
  const setSel = (v: Sel) => (compact ? onOpen?.() : setSelState(v));
  const byId = useMemo(() => new Map(data.places.map((p) => [p.id, p])), [data]);

  // Fit every place into the frame (so "you're here" is always in it), at most 150px a unit so a short story doesn't sprawl.
  const fit = useMemo(() => {
    const pts = [...layout.at.values()];
    const ext = (a: (typeof layout.areas)[number]) => (a.places.length > 1 ? AREA_R * 1.1 : 0.05);
    const xs = layout.areas.flatMap((a) => [a.at.x - ext(a), a.at.x + ext(a)]).concat(pts.map((p) => p.x));
    const ys = layout.areas.flatMap((a) => [a.at.y - ext(a), a.at.y + ext(a)]).concat(pts.map((p) => p.y));
    const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const s = Math.min((w - PAD * 2) / Math.max(x1 - x0, 0.01), (h - PAD * 2) / Math.max(y1 - y0, 0.01), fullscreen ? 600 : 150);
    const ox = (w - (x1 - x0) * s) / 2 - x0 * s, oy = (h - (y1 - y0) * s) / 2 - y0 * s;
    return { s, px: (p: Pt): Pt => ({ x: p.x * s + ox, y: p.y * s + oy }) };
  }, [layout, w, h]);

  // Memory: a place not visited in a while fades (relative to the story's length); here and chapter landmarks never do.
  const span = Math.max(10, data.now / 2);
  const markOpacity = (p: MapPlace) => (p.id === data.here || p.ended.length ? 1 : 1 - 0.55 * Math.min(1, (data.now - p.last) / span));

  // Labels: the most important first (here, landmarks, the most recent); one that would collide stays hidden.
  const labels = useMemo(() => {
    if (!w) return new Map<string, { x: number; y: number; anchor: "start" | "end" | "middle"; b: Box }>();
    const ctx = document.createElement("canvas").getContext("2d");
    const family = box.current ? getComputedStyle(box.current).fontFamily : "serif";
    if (ctx) ctx.font = `${FONT_PX}px ${family}`;
    const measure = (t: string) => (ctx ? ctx.measureText(t).width : t.length * FONT_PX * 0.55);
    const taken: Box[] = [...layout.at.values()].map((p) => { const q = fit.px(p); return { x: q.x - 8, y: q.y - 8, w: 16, h: 16 }; });
    const out = new Map<string, { x: number; y: number; anchor: "start" | "end" | "middle"; b: Box }>();
    const rank = (p: MapPlace) => (p.id === data.here ? 3e6 : 0) + (p.ended.length ? 2e6 : 0) + p.last;
    for (const p of [...data.places].sort((a, b) => rank(b) - rank(a))) {
      const q = fit.px(layout.at.get(p.id)!);
      const tw = measure(p.name), th = FONT_PX + 2;
      const cands: { x: number; y: number; anchor: "start" | "end" | "middle"; b: Box }[] = [
        { x: q.x + 12, y: q.y + 4.5, anchor: "start", b: { x: q.x + 11, y: q.y - th / 2, w: tw + 2, h: th } },
        { x: q.x - 12, y: q.y + 4.5, anchor: "end", b: { x: q.x - 13 - tw, y: q.y - th / 2, w: tw + 2, h: th } },
        { x: q.x, y: q.y + 23, anchor: "middle", b: { x: q.x - tw / 2 - 1, y: q.y + 12, w: tw + 2, h: th } },
        { x: q.x, y: q.y - 14, anchor: "middle", b: { x: q.x - tw / 2 - 1, y: q.y - 26, w: tw + 2, h: th } },
      ];
      const ok = cands.find((c) => c.b.x >= 2 && c.b.y >= 2 && c.b.x + c.b.w <= w - 2 && c.b.y + c.b.h <= h - 2 && !taken.some((t) => hit(t, c.b)));
      if (ok) { out.set(p.id, { x: ok.x, y: ok.y, anchor: ok.anchor, b: ok.b }); taken.push(ok.b); }
    }
    return out;
  }, [data, layout, fit, w, h]);

  // Plates pinned where they were drawn (the newest few), mounted small beside their place.
  const pins = useMemo(() => {
    const size = compact ? 28 : 40;
    return data.plates.slice(0, compact ? 2 : 3).flatMap((pl) => {
      const at = layout.at.get(pl.at);
      if (!at || !w) return [];
      const q = fit.px(at), a0 = unit(`${view.id}:plate:${pl.i}`) * Math.PI * 2;
      const nodes = [...layout.at.values()].map((p) => fit.px(p));
      const spot = (a: number) => {
        const cx = Math.min(w - size / 2 - 6, Math.max(size / 2 + 6, q.x + Math.cos(a) * (size + 14)));
        const cy = Math.min(h - size / 2 - 10, Math.max(size / 2 + 6, q.y + Math.sin(a) * (size + 14)));
        const box = { x: cx - size / 2 - 3, y: cy - size / 2 - 3, w: size + 6, h: size + 12 };
        return { cx, cy, clear: nodes.every((n) => Math.abs(n.x - cx) > size / 2 + 14 || Math.abs(n.y - cy) > size / 2 + 16) && [...labels.values()].every((l) => !hit(l.b, box)) };
      };
      // The first of eight directions whose spot is clear of every place and name (else the first).
      const s0 = Array.from({ length: 8 }, (_, k) => spot(a0 + (k * Math.PI) / 4)).find((sp) => sp.clear) ?? spot(a0);
      return [{ ...pl, q, x: s0.cx - size / 2, y: s0.cy - size / 2, size, tilt: (unit(`${view.id}:tilt:${pl.i}`) - 0.5) * 8 }];
    });
  }, [data, layout, fit, w, h, compact, view.id, labels]);



  if (!data.places.length) return null;
  const here = data.here ? byId.get(data.here) : undefined;
  const threads = data.threads.map((t, i) => ({ ...t, i, at: t.carried ? data.here ?? t.bornAt : t.bornAt ?? data.here })).filter((t) => t.at && layout.at.has(t.at));
  const selPlace = sel?.kind === "place" ? byId.get(sel.id) : undefined;
  const selThread = sel?.kind === "thread" ? data.threads.find((t) => t.id === sel.id) : undefined;
  const canAct = !view.ended;
  const centre = { x: w / 2, y: h / 2 };

  return (
    <div className="lm-nmap">
      <div className="lm-nmap-frame" ref={box} style={size ? { width: w, height: h } : { height: h }}>
        {!fullscreen && w > 0 && (
          <button type="button" className="lm-nmap-big" aria-label="Show the map full screen" title="Full screen"
            onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new Event("weftwake:bigmap")); }}>⤢</button>
        )}
        {w > 0 && (
          <svg ref={svgRef} width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="group" aria-label={`Map of the places you've been${here ? `; you're in ${here.name}` : ""}`}>
            <defs>
              <filter id="lm-nmap-ink" x="-10%" y="-10%" width="120%" height="120%">
                <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves={2} seed={4} result="n" />
                <feDisplacementMap in="SourceGraphic" in2="n" scale={1.8} />
              </filter>
              <filter id="lm-nmap-blot" x="-40%" y="-40%" width="180%" height="180%">
                <feTurbulence type="fractalNoise" baseFrequency="0.35" numOctaves={2} seed={9} result="n" />
                <feDisplacementMap in="SourceGraphic" in2="n" scale={5} />
              </filter>
              <filter id="lm-nmap-smudge" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation={9} /></filter>
              <radialGradient id="lm-nmap-vig" cx="50%" cy="46%" r="72%"><stop offset="55%" style={{ stopColor: "var(--nmap-back)", stopOpacity: 0 }} /><stop offset="100%" style={{ stopColor: "var(--nmap-back)", stopOpacity: 0.9 }} /></radialGradient>
            </defs>
            {/* The painted ground: the dream's image, from the mood of the places (never their layout). */}
            {ground && <image href={ground} x={0} y={0} width={w} height={h} preserveAspectRatio="xMidYMid slice" className={`lm-nmap-ground${develop ? " lm-nmap-develop" : ""}`} aria-hidden="true" />}
            {ground && <rect x={0} y={0} width={w} height={h} fill="url(#lm-nmap-vig)" aria-hidden="true" />}
            {/* Places only heard of: a smudge at the edge, out past where you've been. */}
            {data.rumours.map((r) => {
              const a = unit(`${view.id}:rumour:${r.id}`) * Math.PI * 2;
              const k = Math.min(Math.abs((w / 2 - 18) / Math.cos(a) || Infinity), Math.abs((h / 2 - 18) / Math.sin(a) || Infinity));
              const x = centre.x + Math.cos(a) * k, y = centre.y + Math.sin(a) * k;
              return (
                <g key={r.id} aria-hidden="true" className="lm-nmap-rumour">
                  <ellipse cx={x} cy={y} rx={r.goal ? 30 : 22} ry={r.goal ? 20 : 15} filter="url(#lm-nmap-smudge)" />
                  <text x={x} y={y + 5} textAnchor="middle">?</text>
                </g>
              );
            })}
            {/* Areas with several places: a faint ring that holds them. */}
            {layout.areas.filter((a) => a.places.length > 1).map((a) => {
              const c = fit.px(a.at);
              return <circle key={a.id} className="lm-nmap-area" cx={c.x} cy={c.y} r={AREA_R * 1.1 * fit.s} aria-hidden="true" />;
            })}
            {/* The journey, in the order you went. */}
            {data.route.map((st, k) => {
              const a = fit.px(layout.at.get(st.from)!), b = fit.px(layout.at.get(st.to)!);
              const fade = Math.min(markOpacity(byId.get(st.from)!), markOpacity(byId.get(st.to)!));
              const d = bow(a, b, ((k * 7 + st.turn) % 2 ? 1 : -1) * (st.via === "inside" ? 0.1 : 0.18));
              return (
                <g key={`${st.turn}-${k}`} aria-hidden="true" style={{ opacity: fade }}>
                  <path className={`lm-nmap-route lm-nmap-${st.via}`} d={d} filter={st.via === "elsewhere" ? undefined : "url(#lm-nmap-ink)"} />
                  {st.when && st.via === "elsewhere" && <text className="lm-nmap-when" x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 8} textAnchor="middle">{st.when}</text>}
                </g>
              );
            })}
            {/* Loose ends: a short tuft where each began, in its cloth colour and its own dash. */}
            {threads.map((t) => {
              const q = fit.px(layout.at.get(t.at!)!);
              const a = unit(`${view.id}:thread:${t.id}`) * Math.PI * 2;
              const from = { x: q.x + Math.cos(a) * 10, y: q.y + Math.sin(a) * 10 }, to = { x: q.x + Math.cos(a) * 30, y: q.y + Math.sin(a) * 30 };
              const d = bow(from, to, 0.25);
              const frayed = t.quiet >= 6;
              const name = view.threads.find((x) => x.id === t.id)?.name ?? t.name;
              return (
                <g key={t.id} className="lm-nmap-tuft" role="button" tabIndex={0} aria-label={`Loose end: ${name}`}
                  onClick={() => setSel({ kind: "thread", id: t.id })} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSel({ kind: "thread", id: t.id }); } }}>
                  <path d={d} className="lm-nmap-tuft-hit" />
                  <path d={d} style={{ stroke: colors.get(t.id) ?? "currentColor", strokeDasharray: DASHES[t.i % DASHES.length], opacity: frayed ? 0.55 : 0.9, strokeWidth: frayed ? 1.2 : 1.9 }} />
                  {sel?.kind === "thread" && sel.id === t.id && <circle cx={to.x} cy={to.y} r={6} className="lm-nmap-sel" />}
                </g>
              );
            })}
            {/* Plates, pinned where the loom drew them. */}
            {pins.map((pn) => (
              <g key={pn.i} aria-hidden="true" className="lm-nmap-pin">
                <line x1={pn.q.x} y1={pn.q.y} x2={pn.x + pn.size / 2} y2={pn.y + pn.size / 2} className="lm-nmap-pin-line" />
                <g transform={`rotate(${pn.tilt.toFixed(1)} ${pn.x + pn.size / 2} ${pn.y + pn.size / 2})`}>
                  <rect x={pn.x - 2.5} y={pn.y - 2.5} width={pn.size + 5} height={pn.size + 9} className="lm-nmap-pin-mount" />
                  <PinImage src={api.imageUrl(view.id, pn.i)} x={pn.x} y={pn.y} size={pn.size} amber={skin === "phosphor"} />
                </g>
              </g>
            ))}
            {/* Places. */}
            {data.places.map((p) => {
              const q = fit.px(layout.at.get(p.id)!);
              const isHere = p.id === data.here;
              const picked = sel?.kind === "place" && sel.id === p.id;
              const label = labels.get(p.id);
              const choose = () => setSel({ kind: "place", id: p.id });
              return (
                <g key={p.id} className="lm-nmap-place" role="button" tabIndex={0} aria-pressed={picked}
                  aria-label={`${p.name}${isHere ? ", you're here" : ""}${p.gone ? ", gone" : ""}`}
                  onClick={choose} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); choose(); } }}>
                  <circle cx={q.x} cy={q.y} r={22} className="lm-nmap-hit" />
                  <g style={{ opacity: markOpacity(p) }}>
                    {p.gone
                      ? <><circle cx={q.x} cy={q.y} r={6} className="lm-nmap-gone" /><path d={`M${q.x - 5},${q.y + 5}L${q.x + 5},${q.y - 5}`} className="lm-nmap-gone" /></>
                      : <circle cx={q.x} cy={q.y} r={6.5} className="lm-nmap-blot" filter="url(#lm-nmap-blot)" />}
                    {!p.gone && <SoundMark sound={p.sound} at={q} />}
                  </g>
                  {isHere && <><circle cx={q.x} cy={q.y} r={11} className="lm-nmap-ring" /><circle cx={q.x} cy={q.y} r={15.5} className="lm-nmap-ring lm-nmap-ring2" /></>}
                  {picked && <circle cx={q.x} cy={q.y} r={19} className="lm-nmap-sel" />}
                  {label && <text x={label.x} y={label.y} textAnchor={label.anchor} className={`lm-nmap-label${isHere ? " lm-nmap-label-here" : ""}`}>{p.name}</text>}
                </g>
              );
            })}
            {/* Something sudden struck here. */}
            {data.events.map((e) => {
              const q = fit.px(layout.at.get(e.at)!);
              const x = q.x - 12, y = q.y - 12;
              return <path key={e.turn} aria-hidden="true" className="lm-nmap-knot" d={`M${x},${y - 5}L${x + 1.3},${y - 1.3}L${x + 5},${y}L${x + 1.3},${y + 1.3}L${x},${y + 5}L${x - 1.3},${y + 1.3}L${x - 5},${y}L${x - 1.3},${y - 1.3}Z`} />;
            })}
          </svg>
        )}
      </div>
      {!compact && <div className="lm-nmap-bar" aria-live="polite">
        {selPlace ? <PlaceBar p={selPlace} hereArea={here?.area} isHere={selPlace.id === data.here} crisis={!!view.chapter?.crisis} canAct={canAct}
          onLook={onLook ? () => { onClose(); onLook(selPlace.name); } : undefined}
          onGo={onFill ? (text) => { onClose(); onFill(text); } : undefined} />
          : selThread ? (
            <>
              <p><strong>{view.threads.find((x) => x.id === selThread.id)?.name ?? selThread.name}</strong> <span className="lm-map-meta">{selThread.quiet > 1 ? `quiet for ${selThread.quiet} turns` : "in play now"}{selThread.carried ? " · it goes where you go" : ""}</span></p>
              {selThread.quiet > 1 && onPull && canAct && (() => {
                const t = view.threads.find((x) => x.id === selThread.id);
                return t ? <div className="lm-nmap-actions"><button type="button" onClick={() => { onClose(); onPull(t); }}>Bring it back · uses a turn</button></div> : null;
              })()}
            </>
          )
          : <p className="lm-map-meta">Tap a place to look closer or go back; tap a loose end to bring it back.</p>}
      </div>}
      {!compact && !fullscreen && (
        <div className="lm-nmap-save">
          <label><input type="checkbox" checked={names} onChange={(e) => setNames(e.target.checked)} /> Names on the saved map</label>
          <button type="button" disabled={saving} onClick={() => {
            const el = svgRef.current, frame = box.current;
            if (!el || !frame) return;
            setSaving(true);
            saveMap(el, { title: view.title, code: storyCode(view.id), names, painted: !!ground, back: getComputedStyle(frame).backgroundColor })
              .catch(() => {}).finally(() => setSaving(false));
          }}>{saving ? "Saving…" : "Save the map"}</button>
        </div>
      )}
      {!compact && !fullscreen && <MapKey />}
    </div>
  );
}

const KEY_SEEN = "loom:map-key-seen";

/**
 * How to read the map, in plain words beside each mark. Open by itself the first time a player sees the map (then
 * remembered on this device), a link to open it after that.
 */
function MapKey() {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(KEY_SEEN) !== "1"; } catch { return false; } });
  useEffect(() => { if (open) try { localStorage.setItem(KEY_SEEN, "1"); } catch { /* shown again next time */ } }, [open]);
  const mark = (child: React.ReactNode) => <svg viewBox="0 0 34 20" aria-hidden="true">{child}</svg>;
  const rows: [React.ReactNode, string][] = [
    [mark(<path className="lm-nmap-route lm-nmap-walk" d="M3,13 Q17,3 31,10" />), "The line is your journey, in the order you went."],
    [mark(<path className="lm-nmap-route lm-nmap-elsewhere" d="M3,13 Q17,3 31,10" />), "A dotted line is a jump with /elsewhere, with the time it took if you said."],
    [mark(<><circle cx="17" cy="10" r="4" className="lm-nmap-blot" /><circle cx="17" cy="10" r="8" className="lm-nmap-ring" /></>), "The ring is where you are now."],
    [mark(<><circle cx="17" cy="10" r="4.5" className="lm-nmap-gone" /><path d="M13.5,13.5L20.5,6.5" className="lm-nmap-gone" /></>), "A crossed-out place is gone: the story left it behind (the train pulled away), so there's no way back."],
    [mark(<><circle cx="17" cy="10" r="8.5" className="lm-nmap-area" /><circle cx="14" cy="10" r="2.5" className="lm-nmap-blot" /><circle cx="20.5" cy="11" r="2.5" className="lm-nmap-blot" /></>), "A faint circle holds a place and the parts of it you've been to: a station and its waiting room."],
    [mark(<><circle cx="10" cy="10" r="4" className="lm-nmap-blot" /><circle cx="24" cy="10" r="4" className="lm-nmap-blot" style={{ opacity: 0.45 }} /></>), "Places you haven't been back to in a while grow faint, the way memory does; going back inks them again. Names never fade, and neither do places where a chapter ended."],
    [mark(<g className="lm-nmap-rumour"><ellipse cx="17" cy="10" rx="12" ry="7" style={{ filter: "blur(3px)" }} /><text x="17" y="14" textAnchor="middle">?</text></g>), "A ? at the edge is a place you've only heard of. It has no name on the map and no way there until you find it in the story."],
    [mark(<path d="M8,15 Q16,4 27,6" style={{ fill: "none", stroke: "var(--lm-rail-mark)", strokeWidth: 1.9, strokeDasharray: "5 3", strokeLinecap: "round" }} />), "A short coloured stroke is a loose end, where it began (or with you, if it's about something you carry). Tap it to bring it back."],
    [mark(<path className="lm-nmap-knot" d="M17,4L18.5,8.5L23,10L18.5,11.5L17,16L15.5,11.5L11,10L15.5,8.5Z" />), "A small star is where something sudden happened."],
    [mark(<><rect x="10" y="2" width="15" height="17" className="lm-nmap-pin-mount" /><rect x="11.5" y="3.5" width="12" height="11" style={{ fill: "currentColor", opacity: 0.55 }} /></>), "A small print is one of your plates, pinned where you were when the loom drew it."],
    [mark(<rect x="3" y="3" width="28" height="14" rx="2" style={{ fill: "currentColor", opacity: 0.18 }} />), "The painted background comes with your dreams, from the mood of the places you've been. It's atmosphere only: it never shows where things are."],
  ];
  return (
    <div className="lm-nmap-key">
      <button type="button" className="lm-nmap-key-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? "Hide the key" : "How to read the map"}</button>
      {open && (
        <div className="lm-nmap-key-body">
          <ul>{rows.map(([m, t]) => <li key={t}>{m}<span>{t}</span></li>)}</ul>
          <p>The map grows as you play: each new place is added along your path, and a place never moves once it's on the map. It shows only places you've stood in. The mark inside each place comes from how it sounds there: rails, rain, a lit window, waves, wind. ⤢ in the corner shows the map full screen; zoom in with + to spread it out and see every name.</p>
        </div>
      )}
    </div>
  );
}

function PlaceBar({ p, isHere, hereArea, crisis, canAct, onLook, onGo }: {
  p: MapPlace; isHere: boolean; hereArea: string | undefined; crisis: boolean; canAct: boolean; onLook?(): void; onGo?(text: string): void;
}) {
  // Inside the area you're in it's a walk; anywhere else a /elsewhere jump (time passes on the way).
  const near = !!hereArea && p.area === hereArea;
  const state = isHere ? "you're here" : p.gone ? "gone: the story left it behind" : `last here at turn ${p.visits.at(-1) ?? p.last}`;
  return (
    <>
      <p><strong>{p.name}</strong> <span className="lm-map-meta">{state}</span></p>
      <div className="lm-nmap-actions">
        {onLook && <button type="button" onClick={onLook}>Look closer</button>}
        {onGo && canAct && !isHere && !p.gone && (
          <button type="button" onClick={() => onGo(near ? `I go to ${withArticle(p.name)}.` : `/elsewhere ${p.name}`)}>Go there · uses a turn</button>
        )}
      </div>
      {onGo && canAct && !isHere && !p.gone && !near && (
        <p className="lm-map-meta">{crisis ? "This is the moment of decision: leaving now turns away from it." : "Time passes on the way; what was pressing there has moved on."}</p>
      )}
    </>
  );
}

/** A place's mark, from what it sounds like (rails, rain, a lit window, waves, wind). */
function SoundMark({ sound, at }: { sound: MapPlace["sound"]; at: Pt }) {
  const { x, y } = at;
  if (sound === "train") return <path className="lm-nmap-mark" d={`M${x - 3.5},${y - 2}h7M${x - 3.5},${y + 2}h7`} />;
  if (sound === "rain") return <path className="lm-nmap-mark" d={`M${x - 2.5},${y - 3}l-1.5,5M${x + 0.5},${y - 3}l-1.5,5M${x + 3.5},${y - 3}l-1.5,5`} />;
  if (sound === "fire" || sound === "house" || sound === "cafe" || sound === "jazz") return <rect className="lm-nmap-mark-fill" x={x - 2} y={y - 2} width={4} height={4} />;
  if (sound === "sea" || sound === "water") return <path className="lm-nmap-mark" d={`M${x - 4},${y + 1} q2,-3 4,0 t4,0`} />;
  if (sound === "wind" || sound === "night") return <path className="lm-nmap-mark" d={`M${x - 4},${y - 1.5}h5q2,0 2,-1.5M${x - 4},${y + 1.5}h7`} />;
  if (sound === "city" || sound === "crowd") return <path className="lm-nmap-mark" d={`M${x - 3},${y - 3}v6M${x},${y - 3}v6M${x + 3},${y - 3}v6`} />;
  if (sound === "clock") return <path className="lm-nmap-mark" d={`M${x},${y - 3}v3h2.5`} />;
  return null;
}

/** A thing's description up to its first full stop (cards never cut a sentence off). */
const firstSentence = (hint: string) => (/^.*?[.!?](?=\s|$)/.exec(hint.trim())?.[0] ?? hint).trim();
const STOP = new Set(["the", "a", "an", "your", "you", "my", "of", "and", "in", "on", "with", "late", "old", "its", "his", "her", "their", "that", "this"]);
const words = (s: string) => new Set(s.toLowerCase().split(/[^\p{L}\p{N}'’]+/u).filter((w) => w.length > 2 && !STOP.has(w)));

/**
 * "Where you are", beside the story (Woven's side, Manuscript's margins, Phosphor's panel) and in a sheet from the place
 * chip above the line (owner + UI and game reviews, 2026-10-18). What you can act on comes first: what you carry ("On
 * you", things the canon pass marked carried), then cards for the things in the place you're in (or the things you
 * came across here), the ones a loose end is about first (with its name, in its cloth colour), then ones you haven't
 * looked at, then the latest; each card has its whole first sentence and a tap looks closer. The map, small, last.
 */
export function WhereYouAre({ view, onLook, title = "Where you are", className = "", noMap = false }: {
  view: StoryView; onLook(name: string): void; title?: string; className?: string; noMap?: boolean;
}) {
  const data = useMemo(() => mapOf(view), [view]);
  const colors = useMemo(() => threadColors(view, skinNow()), [view]);
  const open = () => window.dispatchEvent(new CustomEvent("weftwake:map", { detail: "places" }));
  if (!data.places.length) return null;
  const here = data.places.find((p) => p.id === data.here);
  const inside = here ? view.things.filter((t) => t.kind !== "place" && t.parent === here.id && !t.carried) : [];
  const turnsHere = new Set(view.turns.filter((t) => here && t.here === here.id).map((t) => t.n));
  const met = here && !inside.length ? view.things.filter((t) => t.kind !== "place" && turnsHere.has(t.turn) && !t.carried) : [];
  const open_threads = view.threads.filter((t) => t.state === "open");
  // The loose end a thing is about (its name or description shares a word with the thread's name).
  const threadOf = (t: { name: string; hint: string }) => { const mine = words(`${t.name} ${t.hint}`); return open_threads.find((th) => [...words(th.name)].some((w) => mine.has(w))); };
  const last = (t: { turn: number; seen: number[] }) => Math.max(t.turn, ...t.seen);
  const cards = (inside.length ? inside : met).map((t) => ({ t, th: threadOf(t) }))
    .sort((x, y) => Number(!!y.th) - Number(!!x.th) || Number(!!x.t.looked) - Number(!!y.t.looked) || last(y.t) - last(x.t)).slice(0, 4);
  const onYou = view.things.filter((t) => t.carried).slice(0, 5);
  return (
    <section className={`lm-where ${className}`} aria-label={title}>
      <p className="lm-where-label">{title}</p>
      <p className="lm-where-place">{here?.name ?? "somewhere the story hasn't named"}</p>
      {onYou.length > 0 && (
        <div className="lm-where-on">
          <p className="lm-where-sub">On you</p>
          <ul>{onYou.map((t) => <li key={t.id}><button type="button" onClick={() => onLook(t.name)} title={`Look closer at ${t.name}`}>{t.name}</button></li>)}</ul>
        </div>
      )}
      {cards.length > 0 && (
        <div className="lm-where-here">
          <p className="lm-where-sub">{inside.length ? "Here" : "Found here"}</p>
          <div className="lm-where-cards">
            {cards.map(({ t, th }, i) => (
              <button key={t.id} type="button" className="lm-where-card" style={{ ["--tilt" as string]: `${[1.2, -1, 0.7, -1.4][i % 4]}deg`, ...(th ? { ["--c" as string]: colors.get(th.id) ?? "currentColor" } : {}) }}
                data-thread={th ? "" : undefined} onClick={() => onLook(t.name)} title={`Look closer at ${t.name}`}>
                <span className="lm-where-card-h">{t.name}</span>
                {t.hint && <span className="lm-where-card-p">{firstSentence(t.hint)}</span>}
                {th && <span className="lm-where-card-th">↳ {th.name}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {!noMap && <NoirMap view={view} compact onOpen={open} onClose={() => {}} />}
      <button type="button" className="lm-where-open" onClick={open}>Open the map ›</button>
    </section>
  );
}

/**
 * The map, full screen: drawn at the screen's size, and zoomed in (+) to spread places out and show every name; drag
 * (or scroll) to move around. The bar beneath still looks closer, goes there or brings a loose end back.
 */
export function BigMap({ view, onClose, onLook, onFill, onPull }: {
  view: StoryView; onClose(): void; onLook?(name: string): void; onFill?(text: string): void; onPull?(t: StoryView["threads"][number]): void;
}) {
  const ZOOMS = [1, 1.5, 2, 3];
  const [z, setZ] = useState(0);
  const pane = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = pane.current;
    if (!el) return;
    const fit = () => setVp({ w: el.clientWidth, h: el.clientHeight });
    const ro = new ResizeObserver(fit); ro.observe(el); fit();
    return () => ro.disconnect();
  }, []);
  // Zooming keeps the middle of the view in the middle.
  const zoom = (d: number) => {
    const el = pane.current, next = Math.max(0, Math.min(ZOOMS.length - 1, z + d));
    if (!el || next === z) return;
    const k = ZOOMS[next]! / ZOOMS[z]!;
    const cx = (el.scrollLeft + el.clientWidth / 2) * k, cy = (el.scrollTop + el.clientHeight / 2) * k;
    setZ(next);
    requestAnimationFrame(() => { el.scrollLeft = cx - el.clientWidth / 2; el.scrollTop = cy - el.clientHeight / 2; });
  };
  // Its keys first (capture), so Escape closes only this, not the journal underneath.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      else if (e.key === "+" || e.key === "=") zoom(1);
      else if (e.key === "-") zoom(-1);
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  });
  // Drag to move around (a mouse; fingers scroll on their own).
  const drag = useRef<{ x: number; y: number; l: number; t: number } | null>(null);
  const k = ZOOMS[z]!;
  return (
    <div className="lm-bigmap" role="dialog" aria-modal="true" aria-label="The map, full screen">
      <header className="lm-bigmap-head">
        <p className="lm-bigmap-title"><span className="lm-where-label">Map</span> {view.title}</p>
        <div className="lm-bigmap-zoom" role="group" aria-label="Zoom">
          <button type="button" onClick={() => zoom(-1)} disabled={z === 0} aria-label="Zoom out">−</button>
          <span aria-live="polite">{Math.round(k * 100)}%</span>
          <button type="button" onClick={() => zoom(1)} disabled={z === ZOOMS.length - 1} aria-label="Zoom in">+</button>
        </div>
        <button type="button" className="lm-bigmap-close" onClick={onClose} aria-label="Close the full-screen map">×</button>
      </header>
      <div className="lm-bigmap-pane" ref={pane}
        onPointerDown={(e) => { if (e.pointerType === "mouse" && pane.current && !(e.target as Element).closest("[role=button]")) drag.current = { x: e.clientX, y: e.clientY, l: pane.current.scrollLeft, t: pane.current.scrollTop }; }}
        onPointerMove={(e) => { const d = drag.current, el = pane.current; if (d && el) { el.scrollLeft = d.l - (e.clientX - d.x); el.scrollTop = d.t - (e.clientY - d.y); } }}
        onPointerUp={() => { drag.current = null; }} onPointerLeave={() => { drag.current = null; }}>
        {vp.w > 0 && (
          <NoirMap view={view} fullscreen size={{ w: Math.round(vp.w * k), h: Math.round((vp.h - 4) * k) }}
            onLook={onLook} onFill={onFill} onPull={onPull} onClose={onClose} />
        )}
      </div>
    </div>
  );
}
